import { Box, Grid2 as Grid, Stack } from '@mui/material';
import type { FormikProps } from 'formik';
import { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import styled from 'styled-components';
import { object, ref, string } from 'yup';
import { Spinner, StyledButton, StyledTextField } from '../../components';
import { useTSPProviders } from '../../services';
import type { IRegisterFormFields, IRegisterFormValues, OnChangeEvent } from '../../types';
import { isValidPostalCodeFormat } from '../../utils';

export const registerSchema = object({
	zip: string()
		.test('len', 'Must match US, Mexico, or Canada postal code.', (val: string | undefined) =>
			isValidPostalCodeFormat(val)
		)
		.typeError('Must match US, Mexico, or Canada postal code.')
		.required(`Postal code is required`),
	companyName: string().required('Company name is required'),
	firstName: string().required('First name is required'),
	email: string().email('Enter a valid email').required('Email is required'),
	confirmEmail: string()
		.required('Confirm your email')
		.oneOf([ref('email')], 'Email does not match'),
});

const Form = styled('form')`
	width: 100%;
	display: flex;
	align-items: center;
	flex-direction: column;
	justify-content: center;
`;

export const RegisterForm = (props: FormikProps<IRegisterFormValues> & { onV2: (token: string) => void }) => {
	const { values, errors, touched, handleChange, isValid, setFieldTouched } = props;
	const fields: IRegisterFormFields = {
		companyName: { name: 'companyName', label: 'Fleet Company Name' },
		companyType: { name: 'companyType', label: 'Company Type', hover: 'Corporation, LLC, etc.' },
		zip: { name: 'zip', label: 'Fleet Company Postal Code' },
		firstName: { name: 'firstName', label: 'First Name' },
		lastName: { name: 'lastName', label: 'Last Name' },
		email: { name: 'email', label: 'Email Address' },
		confirmEmail: { name: 'confirmEmail', label: 'Confirm Email Address' },
	};
	const { tspProviders } = useTSPProviders();
	const [isHumanV2, setIsHumanV2] = useState(false);

	const change = (name: any, e: OnChangeEvent) => {
		e.persist();
		handleChange(e);
		setFieldTouched(name, true, false);
	};

	return (
		<>
			{!tspProviders.isComplete ? (
				<Spinner />
			) : (
				<Form onSubmit={props.handleSubmit}>
					<Grid container columnSpacing={24} sx={{ maxWidth: 700, mx: 'auto' }}>
						<Grid size={{ xs: 12, md: 6 }}>
							<Stack spacing={4}>
								<StyledTextField
									value={values.companyName}
									name={fields.companyName.name}
									label={fields.companyName.label}
									helperText={touched.companyName ? errors.companyName : ''}
									error={touched.companyName && errors.companyName !== undefined}
									onChange={(e: OnChangeEvent) => change(fields.companyName.name, e)}
								/>

								<StyledTextField
									value={values.zip}
									name={fields.zip.name}
									label={fields.zip.label}
									helperText={touched.zip ? errors.zip : ''}
									error={touched.zip && errors.zip !== undefined}
									onChange={(e: OnChangeEvent) => change(fields.zip.name, e)}
								/>

								<StyledTextField
									value={values.firstName}
									name={fields.firstName.name}
									label={fields.firstName.label}
									helperText={touched.firstName ? errors.firstName : ''}
									error={touched.firstName && errors.firstName !== undefined}
									onChange={(e: OnChangeEvent) => change(fields.firstName.name, e)}
								/>

								<StyledTextField
									value={values.email}
									name={fields.email.name}
									label={fields.email.label}
									helperText={touched.email ? errors.email : ''}
									error={touched.email && errors.email !== undefined}
									onChange={(e: OnChangeEvent) => change(fields.email.name, e)}
								/>
							</Stack>
						</Grid>

						<Grid size={{ xs: 12, md: 6 }}>
							<Stack spacing={4}>
								<StyledTextField
									value={values.companyType}
									name={fields.companyType.name}
									label={fields.companyType.label}
									helperText={fields.companyType.hover}
									error={touched.companyType && errors.companyType !== undefined}
									onChange={(e: OnChangeEvent) => change(fields.companyType.name, e)}
								/>

								<Box sx={{ height: 87 }} />

								<StyledTextField
									value={values.lastName}
									name={fields.lastName.name}
									label={fields.lastName.label}
									helperText={touched.lastName ? errors.lastName : ''}
									error={touched.lastName && errors.lastName !== undefined}
									onChange={(e: OnChangeEvent) => change(fields.lastName.name, e)}
								/>

								<StyledTextField
									value={values.confirmEmail}
									name={fields.confirmEmail.name}
									label={fields.confirmEmail.label}
									helperText={touched.confirmEmail ? errors.confirmEmail : ''}
									error={touched.confirmEmail && errors.confirmEmail !== undefined}
									onChange={(e: OnChangeEvent) => change(fields.confirmEmail.name, e)}
								/>
							</Stack>
						</Grid>

						<Grid size={{ xs: 12 }} display="flex" justifyContent="center">
							<ReCAPTCHA
								sitekey={`6LeS1Z4UAAAAAA8YbI_o663eg166GUwB5Vgf2PzN`}
								onChange={(token: string | null) => {
									if (props.onV2) {
										props.onV2(token || '');
									}
									setIsHumanV2(true);
								}}
							/>
						</Grid>
						<Grid size={{ xs: 12 }} display="flex" justifyContent="center">
							<StyledButton
								type="submit"
								disabled={!isValid || !isHumanV2}
								style={{ width: '75%', marginTop: '32px' }}
							>
								Continue
							</StyledButton>
						</Grid>
					</Grid>
				</Form>
			)}
		</>
	);
};
